<template>
  <div class="area-recursos p-4">
    <h5 class="bold mb-4">
      <i class="fas fa-gavel"></i>
      Recursos
    </h5>

    <!-- Loading Inscrições -->
    <div v-if="loadingInscricoes" class="loading-container">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Carregando...</span>
      </div>
      <p class="text-muted mt-3">Carregando inscrições disponíveis...</p>
    </div>

    <div v-else>
      <!-- Inscrições Disponíveis para Recursos -->
      <div class="section-header mb-3">
        <i class="fas fa-clipboard-list"></i>
        <span>Inscrições Disponíveis para Recursos</span>
      </div>

      <!-- Empty State - Sem Inscrições -->
      <div v-if="!inscricoesComPrazoAberto.length" class="empty-state">
        <i class="fas fa-clipboard-list"></i>
        <p class="empty-title">Nenhum Prazo de Recursos disponível No momento</p>
      </div>

      <template v-else>
        <!-- Desktop Table View -->
        <div class="table-responsive d-none d-md-block">
          <table class="table table-hover">
            <thead class="table-dark">
              <tr>
                <th><i class="fas fa-building"></i> Concurso</th>
                <th><i class="fas fa-briefcase"></i> Vaga</th>
                <th><i class="fas fa-calendar-times"></i> Prazo</th>
                <th><i class="fas fa-cogs"></i> Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="inscricao in inscricoesComPrazoAberto" :key="inscricao.id">
                <td>
                  <strong>{{ inscricao.concurso?.titulo || 'N/A' }}</strong>
                  <br>
                  <small class="text-muted">{{ inscricao.concurso?.prefeitura || 'N/A' }}</small>
                </td>
                <td>{{ inscricao.vaga?.cargo || 'N/A' }}</td>
                <td>
                  <span :class="getClassePrazo(inscricao)">
                    {{ getPrazosDisponiveisText(inscricao) }}
                  </span>
                </td>
                <td>
                  <button 
                    class="btn btn-sm btn-primary" 
                    @click="abrirModal(inscricao)"
                    :disabled="!temPrazoAberto(inscricao)">
                    <i class="fas fa-paper-plane"></i>
                    {{ temPrazoAberto(inscricao) ? 'Enviar Recurso' : 'Sem Prazo Aberto' }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Mobile Card View -->
        <div class="mobile-cards d-md-none">
        <div 
          v-for="inscricao in inscricoesComPrazoAberto" 
          :key="inscricao.id" 
          class="mobile-card"
        >
          <div class="card-header-mobile">
            <i class="fas fa-building"></i>
            <strong>{{ inscricao.concurso?.titulo || 'N/A' }}</strong>
          </div>
          <div class="card-body-mobile">
            <div class="card-item">
              <i class="fas fa-map-marker-alt"></i>
              <span class="label">Prefeitura:</span>
              <span class="value">{{ inscricao.concurso?.prefeitura || 'N/A' }}</span>
            </div>
            <div class="card-item">
              <i class="fas fa-briefcase"></i>
              <span class="label">Vaga:</span>
              <span class="value">{{ inscricao.vaga?.cargo || 'N/A' }}</span>
            </div>
            <div class="card-item">
              <i class="fas fa-calendar-times"></i>
              <span class="label">Prazo:</span>
              <span :class="getClassePrazo(inscricao)" class="value">
                {{ getPrazosDisponiveisText(inscricao) }}
              </span>
            </div>
          </div>
          <div class="card-footer-mobile">
            <button 
              class="btn btn-primary btn-block-mobile" 
              @click="abrirModal(inscricao)"
              :disabled="!temPrazoAberto(inscricao)">
              <i class="fas fa-paper-plane"></i>
              {{ temPrazoAberto(inscricao) ? 'Enviar Recurso' : 'Sem Prazo Aberto' }}
            </button>
          </div>
        </div>
      </div>
      </template>

      <!-- Lista de Recursos Enviados -->
      <div class="section-header mb-3 mt-5">
        <i class="fas fa-list-alt"></i>
        <span>Meus Recursos Enviados</span>
      </div>

      <!-- Loading Recursos -->
      <div v-if="loadingRecursos" class="loading-container">
        <div class="spinner-border text-success" role="status">
          <span class="sr-only">Carregando...</span>
        </div>
        <p class="text-muted mt-3">Carregando recursos enviados...</p>
      </div>

      <!-- Empty State - Sem Recursos -->
      <div v-else-if="!list.length" class="empty-state">
        <i class="fas fa-folder-open"></i>
        <p class="empty-title">Nenhum recurso enviado</p>
        <p class="empty-subtitle">Você ainda não enviou nenhum recurso ou impugnação.</p>
        <div v-if="envioAberto" class="empty-hint">
          <i class="fas fa-arrow-up"></i>
          <span>Use a tabela acima para enviar seu primeiro recurso</span>
        </div>
      </div>

      <!-- Tabela de Recursos - Desktop -->
      <template v-else>
        <div class="table-responsive d-none d-md-block">
          <table class="table table-hover">
            <thead class="table-dark">
              <tr>
                <th><i class="fas fa-calendar"></i> Data</th>
                <th><i class="fas fa-building"></i> Concurso</th>
                <th><i class="fas fa-hashtag"></i> Questão</th>
                <th><i class="fas fa-comment"></i> Fundamentação</th>
                <th><i class="fas fa-info-circle"></i> Status</th>
                <th class="text-center"><i class="fas fa-cogs"></i> Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in list" :key="item.id">
                <td>
                  <span class="badge-date">
                    {{ formatarData(item.created_at) }}
                  </span>
                </td>
                <td>
                  <strong>{{ item.inscricao?.concurso?.prefeitura || 'N/A' }}</strong>
                  <br>
                  <small class="text-muted">{{ item.inscricao?.vaga?.cargo || 'N/A' }}</small>
                </td>
                <td>
                  <span class="badge-questao">
                    Questão {{ item.numero_questao }}
                  </span>
                </td>
                <td>
                  <div class="motivo-container">
                    <p class="motivo-text" :title="item.motivo">
                      {{ limitarTexto(item.motivo, 60) }}
                    </p>
                    <button 
                      v-if="item.motivo && item.motivo.length > 60" 
                      @click="abrirDetalhes(item)" 
                      class="btn-ver-mais"
                    >
                      <i class="fas fa-eye"></i>
                      Ver mais
                    </button>
                  </div>
                </td>
                <td>
                  <div class="status-container">
                    <span :class="getClasseStatus(item.status)" class="badge-status">
                      {{ getStatusText(item.status) }}
                    </span>
                    <div v-if="item.observacao_admin" class="mt-2">
                      <button @click="abrirResposta(item)" class="btn-resposta">
                        <i class="fas fa-reply"></i>
                        Ver Resposta
                      </button>
                    </div>
                  </div>
                </td>
                <td class="text-center">
                  <div class="action-buttons">
                    <a
                      v-if="item.caminho_anexo"
                      :href="item.caminho_anexo"
                      download
                      target="_blank"
                      class="btn btn-sm btn-success"
                      title="Baixar anexo"
                    >
                      <i class="fas fa-download"></i>
                      Download
                    </a>
                    <button
                      v-if="item.status === 'P'"
                      @click="excluir(item)"
                      class="btn btn-sm btn-danger"
                      title="Excluir recurso"
                    >
                      <i class="fas fa-trash-can"></i>
                      Excluir
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Cards de Recursos - Mobile -->
        <div class="mobile-cards-recursos d-md-none">
          <div v-for="item in list" :key="item.id" class="mobile-card-recurso">
            <div class="card-header-recurso">
              <div class="header-left">
                <span class="badge-date">
                  {{ formatarData(item.created_at) }}
                </span>
                <span :class="getClasseStatus(item.status)" class="badge-status">
                  {{ getStatusText(item.status) }}
                </span>
              </div>
            </div>
            <div class="card-body-recurso">
              <div class="card-item">
                <span class="label"><i class="fas fa-building"></i> Concurso:</span>
                <span class="value">{{ item.inscricao?.concurso?.prefeitura || 'N/A' }}</span>
              </div>
              <div class="card-item">
                <span class="label"><i class="fas fa-briefcase"></i> Vaga:</span>
                <span class="value">{{ item.inscricao?.vaga?.cargo || 'N/A' }}</span>
              </div>
              <div class="card-item">
                <span class="label"><i class="fas fa-hashtag"></i> Questão:</span>
                <span class="badge-questao">Questão {{ item.numero_questao }}</span>
              </div>
              <div class="card-item">
                <span class="label"><i class="fas fa-comment"></i> Fundamentação:</span>
                <div class="value">
                  <p class="motivo-text" :title="item.motivo">
                    {{ limitarTexto(item.motivo, 80) }}
                  </p>
                  <button 
                    v-if="item.motivo && item.motivo.length > 80" 
                    @click="abrirDetalhes(item)" 
                    class="btn-ver-mais"
                  >
                    <i class="fas fa-eye"></i>
                    Ver mais
                  </button>
                </div>
              </div>
              <div v-if="item.observacao_admin" class="card-item">
                <button @click="abrirResposta(item)" class="btn-resposta btn-block-mobile">
                  <i class="fas fa-reply"></i>
                  Ver Resposta do Administrador
                </button>
              </div>
            </div>
            <div class="card-footer-recurso">
              <a
                v-if="item.caminho_anexo"
                :href="item.caminho_anexo"
                download
                target="_blank"
                class="btn btn-success btn-block-mobile"
                title="Baixar anexo"
              >
                <i class="fas fa-download"></i>
                Baixar Anexo
              </a>
              <button
                v-if="item.status === 'P'"
                @click="excluir(item)"
                class="btn btn-danger btn-block-mobile"
                title="Excluir recurso"
              >
                <i class="fas fa-trash-can"></i>
                Excluir Recurso
              </button>
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- Modal de Envio de Recurso -->
    <div v-if="showModal" class="modal-overlay" @click="fecharModal">
      <div class="modal-content large" @click.stop>
        <div class="modal-header">
          <h4 class="modal-title">
            <i class="fas fa-paper-plane"></i>
            Enviar Recurso / Impugnação
          </h4>
          <button @click="fecharModal" class="modal-close">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <!-- Loading Overlay no Modal -->
        <div v-if="loadingModal" class="modal-loading-overlay">
          <div class="spinner-border text-primary" role="status">
            <span class="sr-only">Carregando...</span>
          </div>
          <p class="mt-3">Carregando informações...</p>
        </div>

        <div class="modal-body">
          <!-- Informações da Inscrição -->
          <div class="info-box mb-4">
            <h6 class="info-title">
              <i class="fas fa-info-circle"></i>
              Informações da Inscrição
            </h6>
            <div class="info-row">
              <strong><i class="fas fa-building"></i> Concurso:</strong>
              <span>{{ inscricaoSelecionada?.concurso?.prefeitura || 'N/A' }}</span>
            </div>
            <div class="info-row">
              <strong><i class="fas fa-briefcase"></i> Vaga:</strong>
              <span>{{ inscricaoSelecionada?.vaga?.cargo || 'N/A' }}</span>
            </div>
            <div class="info-row">
              <strong><i class="fas fa-id-card"></i> ID da Inscrição:</strong>
              <span>#{{ inscricaoSelecionada?.id || 'N/A' }}</span>
            </div>
          </div>

          <!-- Formulário -->
          <form @submit.prevent="enviarRecurso">
            <!-- Seleção de Prazo -->
            <div class="form-group mb-3">
              <label class="form-label">
                <i class="fas fa-clock"></i>
                Prazo de Recurso <span class="text-danger">*</span>
              </label>
              <select 
                v-model="recurso.prazo_recurso_id" 
                class="form-control"
                required
              >
                <option value="">Selecione um prazo</option>
                <option 
                  v-for="prazo in options" 
                  :key="prazo.id" 
                  :value="prazo.id"
                >
                  {{ prazo.descricao || prazo.titulo || 'Prazo sem descrição' }}
                </option>
              </select>
            </div>

            <!-- Detalhes do Prazo Selecionado -->
            <div v-if="selectedPrazo" class="info-box mb-3">
              <h6 class="info-title">
                <i class="fas fa-calendar-check"></i>
                Detalhes do Prazo Selecionado
              </h6>
              <div class="info-item">
                <i class="fas fa-align-left"></i>
                <span><strong>Descrição:</strong> {{ selectedPrazo.descricao || selectedPrazo.titulo }}</span>
              </div>
              <div class="info-item">
                <i class="fas fa-calendar-alt"></i>
                <span><strong>Início:</strong> {{ formatarDataHora(selectedPrazo.data_inicio) }}</span>
              </div>
              <div class="info-item">
                <i class="fas fa-calendar-times"></i>
                <span><strong>Término:</strong> {{ formatarDataHora(selectedPrazo.data_fim) }}</span>
              </div>
              <div class="info-item" v-if="selectedPrazo.exibir_n_questoes">
                <i class="fas fa-question-circle"></i>
                <span><strong>Tipo de recurso:</strong> exige indicação do número da questão.</span>
              </div>
            </div>

            <!-- Número da Questão (condicional) -->
            <div
              v-if="selectedPrazo && selectedPrazo.exibir_n_questoes"
              class="form-group mb-3"
            >
              <label class="form-label">
                <i class="fas fa-hashtag"></i>
                Número da Questão <span class="text-danger">*</span>
              </label>
              <FieldInput
                type="number"
                :min="1"
                :max="999"
                required
                v-model="recurso.numero_questao"
                placeholder="Ex: 15"
              />
              <small class="form-text text-muted">
                <i class="fas fa-info-circle"></i>
                Informe o número da questão que deseja recursar
              </small>
            </div>

            <!-- Fundamentação -->
            <div class="form-group mb-3">
              <label class="form-label">
                <i class="fas fa-align-left"></i>
                Fundamentação do Recurso <span class="text-danger">*</span>
              </label>
              <FieldInput
                type="textarea"
                required
                v-model="recurso.motivo"
                placeholder="Descreva detalhadamente a fundamentação do seu recurso..."
                rows="6"
              />
              <small class="form-text text-muted">
                <i class="fas fa-info-circle"></i>
                Explique de forma clara e objetiva os motivos do seu recurso
              </small>
            </div>

            <!-- Upload de Arquivo -->
            <div class="form-group mb-3">
              <label class="form-label">
                <i class="fas fa-paperclip"></i>
                Anexo (Opcional)
              </label>
              <div class="file-upload-wrapper">
                <FieldFile
                  accept="application/pdf"
                  key="arquivo-recurso"
                  v-model="file"
                  label=""
                />
                <small class="file-help-text">
                  <i class="fas fa-info-circle"></i>
                  PDF - Tamanho máximo: 5MB
                </small>
              </div>
              <small class="form-text text-muted mt-2">
                <i class="fas fa-info-circle"></i>
                Anexe documentos que fundamentem seu recurso
              </small>
            </div>
          </form>
        </div>

        <div class="modal-footer">
          <button 
            type="button" 
            class="btn btn-secondary" 
            @click="fecharModal"
            :disabled="enviando"
          >
            <i class="fas fa-times"></i>
            Cancelar
          </button>
          <button 
            type="submit" 
            class="btn btn-primary"
            @click="enviarRecurso"
            :disabled="enviando"
          >
            <i :class="enviando ? 'fas fa-spinner fa-spin' : 'fas fa-paper-plane'"></i>
            {{ enviando ? 'Enviando...' : 'Enviar Recurso' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modal de Detalhes do Recurso -->
    <div v-if="showDetalhesModal" class="modal-overlay" @click="fecharDetalhes">
      <div class="modal-content large" @click.stop>
        <div class="modal-header">
          <h4 class="modal-title">
            <i class="fas fa-eye"></i>
            Detalhes do Recurso - Questão {{ recursoSelecionado.numero_questao }}
          </h4>
          <button @click="fecharDetalhes" class="modal-close">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="info-box mb-3">
            <div class="info-row">
              <strong><i class="fas fa-calendar"></i> Data:</strong>
              <span>{{ formatarDataHora(recursoSelecionado.created_at) }}</span>
            </div>
            <div class="info-row">
              <strong><i class="fas fa-hashtag"></i> Questão:</strong>
              <span>{{ recursoSelecionado.numero_questao }}</span>
            </div>
            <div class="info-row">
              <strong><i class="fas fa-info-circle"></i> Status:</strong>
              <span :class="getClasseStatus(recursoSelecionado.status)" class="badge-status">
                {{ getStatusText(recursoSelecionado.status) }}
              </span>
            </div>
          </div>
          
          <div class="detalhe-texto">
            <h6><i class="fas fa-align-left"></i> Fundamentação:</h6>
            <p>{{ recursoSelecionado.motivo }}</p>
          </div>
          
          <div v-if="recursoSelecionado.observacao_admin" class="detalhe-texto mt-3">
            <h6><i class="fas fa-reply"></i> Resposta do Administrador:</h6>
            <p>{{ recursoSelecionado.observacao_admin }}</p>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="fecharDetalhes">
            <i class="fas fa-times"></i>
            Fechar
          </button>
        </div>
      </div>
    </div>

    <!-- Modal de Resposta -->
    <div v-if="showRespostaModal" class="modal-overlay" @click="fecharResposta">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h4 class="modal-title">
            <i class="fas fa-reply"></i>
            Resposta do Recurso
          </h4>
          <button @click="fecharResposta" class="modal-close">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="detalhe-texto resposta-texto">
            {{ recursoSelecionado.resposta }}
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="fecharResposta">
            <i class="fas fa-times"></i>
            Fechar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
import FieldFile from '@/components/fields/FieldFile'
import FieldInput from '@/components/fields/FieldInput'

export default {
  name: 'RecursosImpugnacoes',
  components: {
    FieldInput,
    FieldFile
  },

  data() {
    return {
      inscricoes: [],
      list: [],
      // Todos os prazos (inclusive fechados) para permitir filtragem por inscrição/concurso
      todosPrazosRecursos: [],
      // Prazos disponíveis no select do modal (já filtrados para a inscrição selecionada)
      options: [],
      loadingInscricoes: false,
      loadingRecursos: false,
      loadingPrazos: false,
      loadingModal: false,
      enviando: false,
      envioAberto: false,
      showModal: false,
      showDetalhesModal: false,
      showRespostaModal: false,
      inscricaoSelecionada: null,
      recursoSelecionado: {},
      recurso: {
        prazo_recurso_id: '',
        numero_questao: '',
        motivo: ''
      },
      file: null
    }
  },

  created() {
    // console.log('RecursosImpugnacoes: Componente criado')
    this.carregarInscricoes()
    this.carregarPrazos()
    this.carregarRecursos()
  },

  computed: {
    ...mapGetters({ profile: 'profile/getProfile' }),

    inscricoesAtivas() {
      const statusPermitidos = ['A', 'D', 'I']
      return this.inscricoes.filter(inscricao => statusPermitidos.includes(inscricao.status))
    },

    inscricoesComPrazoAberto() {
      return this.inscricoesAtivas.filter(inscricao => this.temPrazoAberto(inscricao))
    },

    selectedPrazo() {
      if (!this.recurso.prazo_recurso_id) return null
      return this.options.find(p => p.id === this.recurso.prazo_recurso_id)
    },
  },

  methods: {
    async carregarInscricoes() {
      try {
        this.loadingInscricoes = true
        // console.log('Carregando inscrições...')
        
        const { data } = await axios.get('/ps/inscricao')
        this.inscricoes = data || []
        
        // console.log('Inscrições carregadas:', this.inscricoes.length)
      } catch (error) {
        console.error('Erro ao carregar inscrições:', error)
        this.$sweet.sweetMensagem('Erro ao carregar inscrições.')
        this.inscricoes = []
      } finally {
        this.loadingInscricoes = false
      }
    },

    async carregarPrazos() {
      try {
        this.loadingPrazos = true
        const { data } = await axios.get('/ps/recurso/prazos')

        // Guardar todos para filtragem por inscrição/concurso
        this.todosPrazosRecursos = (data || []).map(prazo => ({
          ...prazo,
          label: prazo.descricao || prazo.titulo || 'Prazo sem descrição'
        }))

        // Sinalizador global (mantido), mas a UI agora usa verificação por inscrição
        this.envioAberto = this.temAlgumPrazoAbertoGlobal()
      } catch (error) {
        console.error('Erro ao carregar prazos:', error)
        this.todosPrazosRecursos = []
        this.options = []
        this.envioAberto = false
      } finally {
        this.loadingPrazos = false
      }
    },

    async carregarRecursos() {
      try {
        this.loadingRecursos = true
        // console.log('Carregando recursos...')
        
        // Buscar recursos de todas as inscrições do candidato
        const { data } = await axios.get('/ps/recurso')
        
        this.list = data || []
        
        // console.log('Recursos carregados:', this.list.length)
      } catch (error) {
        console.error('Erro ao carregar recursos:', error)
        this.list = []
        this.$sweet.sweetMensagem('Erro ao carregar recursos.')
      } finally {
        this.loadingRecursos = false
      }
    },

    async abrirModal(inscricao) {
      try {
        this.inscricaoSelecionada = inscricao
        this.showModal = true
        this.loadingModal = false

        // Carregar options do modal filtrados por inscrição (prazos abertos)
        const { prazosAbertos } = this.filtrarPrazosPorInscricao(inscricao)
        this.options = prazosAbertos
        
        // Resetar formulário
        this.recurso = {
          prazo_recurso_id: '',
          numero_questao: '',
          motivo: ''
        }
        this.file = null
      } catch (error) {
        console.error('Erro ao abrir modal:', error)
        this.$sweet.sweetMensagem('Erro ao abrir modal de recurso.')
      }
    },

    fecharModal() {
      this.showModal = false
      this.inscricaoSelecionada = null
      this.recurso = {
        prazo_recurso_id: '',
        numero_questao: '',
        motivo: ''
      }
      this.file = null
      this.options = []
    },

    async enviarRecurso() {
      try {
        // Validações
        if (!this.recurso.prazo_recurso_id) {
          this.$sweet.sweetError('Selecione um prazo de recurso.')
          return
        }

        if (this.selectedPrazo && this.selectedPrazo.exibir_n_questoes && !this.recurso.numero_questao) {
          this.$sweet.sweetError('Informe o número da questão.')
          return
        }

        if (!this.recurso.motivo || !this.recurso.motivo.trim()) {
          this.$sweet.sweetError('Informe a fundamentação do recurso.')
          return
        }

        if (this.file && this.file.size > 5 * 1024 * 1024) {
          this.$sweet.sweetError('O arquivo deve ter no máximo 5MB.')
          return
        }

        this.enviando = true
        this.$sweet.sweetWait('Enviando recurso...')

        const formData = new FormData()
        formData.append('prazo_recurso_id', this.recurso.prazo_recurso_id)
        formData.append('numero_questao', this.recurso.numero_questao)
        formData.append('motivo', this.recurso.motivo)
        
        if (this.file) {
          formData.append('arquivo', this.file)
        }

        const response = await axios.post(
          `/ps/recurso/${this.inscricaoSelecionada.id}/registrar`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }
        )

        if (response.status === 201) {
          this.$sweet.sweetSuccess('Recurso enviado com sucesso!')
          this.fecharModal()
          await this.carregarRecursos()
        } else {
          this.$sweet.sweetMensagem('Erro ao enviar recurso.')
        }
      } catch (error) {
        console.error('Erro ao enviar recurso:', error)
        const mensagem = error.response?.data?.error || 'Erro ao enviar recurso. Tente novamente.'
        this.$sweet.sweetError(mensagem)
      } finally {
        this.enviando = false
      }
    },

    async excluir(item) {
      try {
        const { value } = await this.$sweet.sweetQuestionPersonalizado(
          'Excluir Recurso',
          '<p class="font-15">Realmente deseja excluir este recurso/impugnação?</p>'
        )
        
        if (!value) return

        this.$sweet.sweetWait('Excluindo recurso...')
        
        const response = await axios.delete(`/ps/recurso/${item.id}`)
        
        if (response.status === 200) {
          this.$sweet.sweetSuccess('Recurso excluído com sucesso.')
          await this.carregarRecursos()
        } else {
          this.$sweet.sweetMensagem('Erro ao remover o recurso. Tente novamente.')
        }
      } catch (error) {
        console.error('Erro ao excluir recurso:', error)
        this.$sweet.sweetError('Erro ao excluir recurso.')
      }
    },

    abrirDetalhes(item) {
      this.recursoSelecionado = item
      this.showDetalhesModal = true
    },

    fecharDetalhes() {
      this.showDetalhesModal = false
      this.recursoSelecionado = {}
    },

    abrirResposta(item) {
      this.recursoSelecionado = item
      this.showRespostaModal = true
    },

    fecharResposta() {
      this.showRespostaModal = false
      this.recursoSelecionado = {}
    },

    temAlgumPrazoAbertoGlobal() {
      const agora = new Date()
      return (this.todosPrazosRecursos || []).some(p => {
        const inicio = new Date(p.data_inicio)
        const fim = new Date(p.data_fim)
        return agora >= inicio && agora <= fim
      })
    },

    /**
     * Retorna os prazos filtrados por concurso para uma inscrição específica.
     * Se o prazo não tiver campo `concurso_id`, cai no fallback (não filtra por concurso).
     */
    filtrarPrazosPorInscricao(inscricao) {
      if (!inscricao) return { prazosFiltrados: [], prazosAbertos: [] }

      const agora = new Date()
      const concursoId = inscricao.concurso_id || inscricao.concurso?.id || null

      let prazosFiltrados = this.todosPrazosRecursos || []

      // Filtra por concurso apenas se o backend enviar `concurso_id` no prazo
      if (concursoId && prazosFiltrados.some(p => p.concurso_id !== undefined && p.concurso_id !== null)) {
        prazosFiltrados = prazosFiltrados.filter(p => p.concurso_id === concursoId)
      }

      const prazosAbertos = prazosFiltrados.filter(p => {
        const inicio = new Date(p.data_inicio)
        const fim = new Date(p.data_fim)
        return agora >= inicio && agora <= fim
      })

      return { prazosFiltrados, prazosAbertos }
    },

    temPrazoAberto(inscricao) {
      const { prazosAbertos } = this.filtrarPrazosPorInscricao(inscricao)
      return prazosAbertos.length > 0
    },

    getPrazosDisponiveisText(inscricao) {
      const { prazosAbertos } = this.filtrarPrazosPorInscricao(inscricao)
      return prazosAbertos.length > 0 ? `${prazosAbertos.length} prazo(s) aberto(s)` : 'Sem prazo aberto'
    },

    getClassePrazo(inscricao) {
      return this.temPrazoAberto(inscricao) ? 'badge badge-success' : 'badge badge-secondary'
    },

    getClasseStatus(status) {
      const classes = {
        'P': 'badge-warning',  // Pendente
        'D': 'badge-success',  // Deferido
        'I': 'badge-danger'    // Indeferido
      }
      return classes[status] || 'badge-secondary'
    },

    getStatusText(status) {
      const statusMap = {
        'P': 'Em Análise',
        'D': 'Deferido',
        'I': 'Indeferido'
      }
      return statusMap[status] || 'Desconhecido'
    },

    limitarTexto(texto, limite) {
      if (!texto) return '-'
      return texto.length > limite ? texto.substring(0, limite) + '...' : texto
    },

    formatarData(data) {
      if (!data) return '-';
      
      try {
        // Se a data vier apenas com a data (sem hora), usar diretamente
        if (typeof data === 'string' && data.match(/^\d{4}-\d{2}-\d{2}$/)) {
          const [year, month, day] = data.split('-');
          return `${day}/${month}/${year}`;
        }
        
        // Tratar formato do banco: YYYY-MM-DD HH:mm:ss (com espaço)
        if (typeof data === 'string') {
          let dateString = data.replace('Z', '');
          
          // Se tiver formato com espaço (formato do banco: YYYY-MM-DD HH:mm:ss)
          if (dateString.match(/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}/)) {
            const [datePart] = dateString.split(' ');
            const [year, month, day] = datePart.split('-');
            return `${day}/${month}/${year}`;
          }
          
          // Para datas ISO com timezone, remover o 'Z' e tratar como local
          if (dateString.includes('T')) {
            dateString = dateString.replace('Z', '');
            if (dateString.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
              const [datePart] = dateString.split('T');
              const [year, month, day] = datePart.split('-');
              return `${day}/${month}/${year}`;
            }
          }
        }
        
        // Criar data e usar métodos locais
        const dateObj = new Date(data);
        if (isNaN(dateObj.getTime())) return '-';
        
        const dia = String(dateObj.getDate()).padStart(2, '0');
        const mes = String(dateObj.getMonth() + 1).padStart(2, '0');
        const ano = dateObj.getFullYear();
        return `${dia}/${mes}/${ano}`;
      } catch (error) {
        return '-';
      }
    },

    formatarDataHora(data) {
      if (!data) return '-';
      
      try {
        // Se a data vier apenas com a data (sem hora), usar diretamente
        if (typeof data === 'string' && data.match(/^\d{4}-\d{2}-\d{2}$/)) {
          const [year, month, day] = data.split('-');
          return `${day}/${month}/${year} 00:00`;
        }
        
        // Tratar formato do banco: 2025-11-25 07:53:15.000
        if (typeof data === 'string') {
          // Remover o 'Z' do final se existir (formato ISO)
          let dateString = data.replace('Z', '');
          
          // Se tiver formato com espaço (formato do banco: YYYY-MM-DD HH:mm:ss)
          if (dateString.match(/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}/)) {
            const [datePart, timePart] = dateString.split(' ');
            const [year, month, day] = datePart.split('-');
            const [hours, minutes] = timePart.split(':');
            return `${day}/${month}/${year} ${hours}:${minutes}`;
          }
          
          // Se tiver formato ISO com T (YYYY-MM-DDTHH:mm:ss)
          if (dateString.includes('T')) {
            const [datePart, timePart] = dateString.split('T');
            const [year, month, day] = datePart.split('-');
            const [hours, minutes] = timePart.split(':');
            return `${day}/${month}/${year} ${hours}:${minutes}`;
          }
        }
        
        // Fallback: usar Date object apenas se necessário
        const dateObj = new Date(data);
        if (isNaN(dateObj.getTime())) return '-';
        
        const dia = String(dateObj.getDate()).padStart(2, '0');
        const mes = String(dateObj.getMonth() + 1).padStart(2, '0');
        const ano = dateObj.getFullYear();
        const hora = String(dateObj.getHours()).padStart(2, '0');
        const minuto = String(dateObj.getMinutes()).padStart(2, '0');
        return `${dia}/${mes}/${ano} ${hora}:${minuto}`;
      } catch (error) {
        return '-';
      }
    }
  }
}
</script>

<style lang="sass" scoped>
.area-recursos
  background-color: #fff
  padding: 25px 15px
  border-radius: 8px
  box-shadow: 0 2px 4px rgba(0,0,0,0.1)
  
  h5
    font-size: 20px
    font-weight: 600
    text-align: center
    color: #2c3e50
    margin-bottom: 25px
    
    i
      margin-right: 10px
      color: #007bff

// Section headers
.section-header
  font-size: 16px
  font-weight: 600
  color: #2c3e50
  padding: 10px 15px
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)
  border-left: 4px solid #007bff
  border-radius: 4px
  margin-bottom: 20px
  
  i
    margin-right: 8px
    color: #007bff
  
  span
    vertical-align: middle

// Formulário de recurso
.recurso-form-container
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)
  padding: 30px
  border-radius: 8px
  box-shadow: 0 2px 4px rgba(0,0,0,0.1)

// Form elements
.form-group
  margin-bottom: 20px
  
  .form-label
    font-weight: 600
    color: #2c3e50
    margin-bottom: 8px
    display: block
    
    i
      margin-right: 6px
      color: #007bff
    
    .text-danger
      margin-left: 2px

.form-control
  border-radius: 6px
  border: 1px solid #ced4da
  padding: 10px 12px
  transition: all 0.3s ease
  
  &:focus
    border-color: #007bff
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25)
  
  &.is-invalid
    border-color: #dc3545
    
    &:focus
      box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25)

.form-text
  font-size: 12px
  margin-top: 5px

// File upload wrapper
.file-upload-wrapper
  position: relative
  
  // Ocultar label padrão do FieldFile
  ::v-deep .label
    display: none !important
  
  // Ajustar o input file
  ::v-deep input[type="file"]
    width: 100% !important
    height: 45px !important
    min-height: 45px !important
    max-height: none !important
    padding: 10px 12px !important
    padding-left: 8px !important
    border: 2px solid #ced4da !important
    border-radius: 6px !important
    font-size: 14px !important
    display: block !important
    box-sizing: border-box !important
    transition: all 0.3s ease !important
    cursor: pointer !important
    
    &:hover
      border-color: #007bff !important
      background-color: #f8f9fa !important
    
    &:focus
      border-color: #007bff !important
      box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25) !important
      outline: none !important
  
  // Sobrescrever a classe max-25-px
  ::v-deep .max-25-px
    max-height: none !important
    height: 45px !important
    min-height: 45px !important
  
  // Estilo para o texto de ajuda
  .file-help-text
    display: block
    width: 100%
    text-align: right
    font-size: 11px
    color: #dc3545
    margin-top: 3px
    
    i
      margin-right: 3px

// Alerts
.alert
  padding: 20px
  border-radius: 8px
  margin-bottom: 25px
  border: none
  box-shadow: 0 2px 8px rgba(0,0,0,0.1)
  animation: slideInDown 0.4s ease
  
  &.alert-warning
    background: linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%)
    border-left: 5px solid #ffc107
    
    .alert-content
      display: flex
      align-items: flex-start
      gap: 15px
      
      i
        font-size: 24px
        color: #ff9800
        margin-top: 2px
      
      strong
        display: block
        font-size: 16px
        color: #856404
        margin-bottom: 5px
      
      p
        color: #856404
        font-size: 14px
        line-height: 1.5

// Loading container
.loading-container
  text-align: center
  padding: 80px 20px
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)
  border-radius: 8px
  animation: fadeIn 0.3s ease
  
  .spinner-border
    width: 3rem
    height: 3rem
    border-width: 0.3rem
  
  p
    font-size: 15px
    color: #6c757d
    margin-top: 15px

// Empty state
.empty-state
  text-align: center
  padding: 70px 30px
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)
  border-radius: 12px
  margin: 20px 0
  box-shadow: 0 2px 8px rgba(0,0,0,0.08)
  animation: fadeIn 0.5s ease
  
  i.fa-folder-open
    font-size: 64px
    color: #cbd5e0
    margin-bottom: 20px
    display: block
    animation: pulse 2s ease-in-out infinite
  
  .empty-title
    font-size: 18px
    font-weight: 600
    color: #495057
    margin: 15px 0 10px 0
  
  .empty-subtitle
    font-size: 14px
    color: #6c757d
    margin: 0 0 20px 0
  
  .empty-hint
    display: inline-flex
    align-items: center
    gap: 8px
    padding: 10px 20px
    background: rgba(0, 123, 255, 0.1)
    border-radius: 20px
    color: #007bff
    font-size: 13px
    margin-top: 10px
    
    i
      font-size: 14px
      animation: bounce 2s ease-in-out infinite

// Tables
.table-responsive
  margin-top: 15px
  border-radius: 8px
  overflow: hidden
  box-shadow: 0 2px 4px rgba(0,0,0,0.1)

  @media (max-width: 767px)
    display: none !important

.table
  margin-bottom: 0
  
  thead.table-dark
    background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%)
    
    th
      color: #fff
      font-weight: 600
      border: none
      padding: 12px
      font-size: 14px
      
      i
        margin-right: 5px
        opacity: 0.8
  
  tbody
    tr
      transition: all 0.3s ease
      
      &:hover
        background-color: #f8f9fa
        transform: scale(1.005)
    
    td
      padding: 12px
      vertical-align: middle
      font-size: 14px

.badge-date, .badge-questao
  background-color: #6c757d
  color: #fff
  padding: 5px 10px
  border-radius: 12px
  font-size: 12px
  font-weight: 600
  display: inline-block

.badge-questao
  background-color: #17a2b8

// Badge de prazo (usado na tabela de inscrições)
.badge
  padding: 5px 12px
  border-radius: 12px
  font-size: 12px
  font-weight: 600
  display: inline-block
  
  &.badge-success
    background-color: #28a745
    color: #fff !important
  
  &.badge-secondary
    background-color: #6c757d
    color: #fff !important

.badge-status
  padding: 5px 12px
  border-radius: 12px
  font-size: 12px
  font-weight: 600
  display: inline-block
  
  &.badge-warning
    background-color: #ffc107
    color: #000
  
  &.badge-success
    background-color: #28a745
    color: #fff
  
  &.badge-danger
    background-color: #dc3545
    color: #fff

.motivo-container
  .motivo-text
    margin: 0
    font-size: 13px
    color: #2c3e50
    line-height: 1.4

.btn-ver-mais, .btn-resposta
  background: none
  border: none
  color: #007bff
  cursor: pointer
  font-size: 12px
  padding: 4px 8px
  margin-top: 5px
  border-radius: 4px
  transition: all 0.2s ease
  
  i
    margin-right: 4px
  
  &:hover
    background: #e7f3ff
    text-decoration: underline

.status-container
  display: flex
  flex-direction: column
  gap: 8px

.action-buttons
  display: flex
  gap: 8px
  justify-content: center
  flex-wrap: wrap


// Modal customizado
.modal-overlay
  position: fixed
  top: 0
  left: 0
  width: 100%
  height: 100%
  background: rgba(0, 0, 0, 0.5)
  z-index: 1050
  display: flex
  align-items: center
  justify-content: center
  padding: 20px
  animation: fadeIn 0.3s ease

.modal-content
  background: white
  border-radius: 10px
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3)
  max-width: 600px
  width: 90vw
  max-height: 90vh
  overflow: auto
  position: relative
  animation: slideDown 0.3s ease
  
  &.large
    max-width: 800px

.modal-header
  display: flex
  justify-content: space-between
  align-items: center
  padding: 20px 25px
  border-bottom: 1px solid #e9ecef
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)
  border-radius: 10px 10px 0 0
  
  .modal-title
    margin: 0
    font-size: 18px
    font-weight: 600
    color: #2c3e50
    
    i
      margin-right: 8px
      color: #007bff

.modal-close
  background: none
  border: none
  font-size: 20px
  color: #6c757d
  cursor: pointer
  padding: 5px
  border-radius: 50%
  width: 35px
  height: 35px
  display: flex
  align-items: center
  justify-content: center
  transition: all 0.3s ease
  
  &:hover
    background: #e9ecef
    color: #dc3545

// Loading overlay dentro do modal
.modal-loading-overlay
  position: absolute
  top: 0
  left: 0
  width: 100%
  height: 100%
  background: rgba(255, 255, 255, 0.95)
  display: flex
  flex-direction: column
  align-items: center
  justify-content: center
  z-index: 10
  border-radius: 10px
  
  .spinner-border
    width: 3rem
    height: 3rem
    border-width: 0.3rem
  
  p
    margin-top: 15px
    color: #6c757d
    font-size: 15px

.modal-body
  padding: 25px
  max-height: 70vh
  overflow-y: auto
  
  .info-box
    background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)
    padding: 15px 20px
    border-radius: 8px
    border-left: 5px solid #2196f3
    margin-bottom: 20px
    
    .info-title
      font-size: 15px
      font-weight: 600
      color: #2c3e50
      margin-bottom: 12px
      display: flex
      align-items: center
      gap: 8px
    
    .info-row
      display: flex
      justify-content: space-between
      align-items: center
      padding: 8px 0
      border-bottom: 1px solid rgba(255,255,255,0.5)
      
      &:last-child
        border-bottom: none
      
      strong
        color: #2c3e50
        font-size: 14px
        
        i
          margin-right: 6px
      
      span
        color: #495057
        font-size: 14px
    
    .info-item
      display: flex
      align-items: flex-start
      gap: 10px
      padding: 6px 0
      
      i
        color: #2196f3
        margin-top: 3px
        flex-shrink: 0
      
      span
        color: #495057
        font-size: 14px
        line-height: 1.5
        
        strong
          color: #2c3e50
  
  .detalhe-texto
    background: #f8f9fa
    padding: 15px
    border-radius: 6px
    border-left: 4px solid #007bff
    
    h6
      margin: 0 0 10px 0
      font-size: 14px
      font-weight: 600
      color: #2c3e50
      
      i
        margin-right: 6px
    
    p
      margin: 0
      line-height: 1.6
      color: #495057
    
    &.resposta-texto
      border-left-color: #28a745
      background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%)

.modal-footer
  display: flex
  justify-content: flex-end
  gap: 10px
  padding: 20px 25px
  border-top: 1px solid #e9ecef
  background: #f8f9fa
  border-radius: 0 0 10px 10px

// Buttons
.btn
  border-radius: 6px
  padding: 10px 20px
  font-weight: 600
  transition: all 0.3s ease
  border: none
  font-size: 14px
  
  &:hover:not(:disabled)
    transform: translateY(-2px)
    box-shadow: 0 4px 12px rgba(0,0,0,0.2)
  
  &:active:not(:disabled)
    transform: translateY(0)
  
  &:disabled
    opacity: 0.6
    cursor: not-allowed
    transform: none
  
  i
    margin-right: 8px
  
  &.btn-sm
    padding: 6px 12px
    font-size: 13px
    
    i
      margin-right: 5px
  
  &.btn-lg
    padding: 12px 30px
    font-size: 16px
    
    i
      margin-right: 10px
  
  &.btn-primary
    background: linear-gradient(135deg, #007bff 0%, #0056b3 100%)
    
    &:hover:not(:disabled)
      background: linear-gradient(135deg, #0056b3 0%, #004085 100%)
  
  &.btn-success
    background: linear-gradient(135deg, #28a745 0%, #1e7e34 100%)
    
    &:hover:not(:disabled)
      background: linear-gradient(135deg, #1e7e34 0%, #155724 100%)
  
  &.btn-danger
    background: linear-gradient(135deg, #dc3545 0%, #c82333 100%)
    
    &:hover:not(:disabled)
      background: linear-gradient(135deg, #c82333 0%, #bd2130 100%)
  
  &.btn-secondary
    background: linear-gradient(135deg, #6c757d 0%, #5a6268 100%)
    
    &:hover:not(:disabled)
      background: linear-gradient(135deg, #5a6268 0%, #545b62 100%)

// Animations
@keyframes fadeIn
  from
    opacity: 0
  to
    opacity: 1

@keyframes slideDown
  from
    opacity: 0
    transform: translateY(-50px)
  to
    opacity: 1
    transform: translateY(0)

@keyframes slideInDown
  from
    opacity: 0
    transform: translateY(-20px)
  to
    opacity: 1
    transform: translateY(0)

@keyframes pulse
  0%, 100%
    transform: scale(1)
  50%
    transform: scale(1.05)

@keyframes bounce
  0%, 100%
    transform: translateY(0)
  50%
    transform: translateY(-5px)

// Utility classes
.weight-400
  font-weight: 400

// Mobile Cards
.mobile-cards
  display: flex
  flex-direction: column
  gap: 15px
  margin-top: 15px

.mobile-card
  background: #fff
  border-radius: 8px
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1)
  overflow: hidden
  transition: all 0.3s ease
  
  &:hover
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15)
    transform: translateY(-2px)

.card-header-mobile
  background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%)
  color: #fff
  padding: 15px
  display: flex
  align-items: center
  gap: 10px
  
  i
    font-size: 18px
    opacity: 0.9
  
  strong
    font-size: 14px
    font-weight: 600
    flex: 1
    line-height: 1.4

.card-body-mobile
  padding: 15px
  
  .card-item
    display: flex
    align-items: flex-start
    margin-bottom: 12px
    gap: 8px
    flex-wrap: wrap
    
    &:last-child
      margin-bottom: 0
    
    i
      color: #007bff
      font-size: 16px
      flex-shrink: 0
      min-width: 18px
      margin-top: 2px
    
    .label
      font-weight: 600
      color: #6c757d
      font-size: 13px
      flex-shrink: 0
      min-width: fit-content
    
    .value
      flex: 1
      color: #2c3e50
      font-size: 14px
      line-height: 1.4
      text-align: left
      word-break: break-word
      min-width: 0
      overflow-wrap: break-word

.card-footer-mobile
  padding: 15px
  background: #f8f9fa
  border-top: 1px solid #e9ecef
  
  .btn-block-mobile
    width: 100%
    padding: 12px
    font-size: 14px
    font-weight: 600
    display: flex
    align-items: center
    justify-content: center
    gap: 8px
    
    i
      font-size: 16px

// Responsividade
@media (max-width: 768px)
  .modal-content
    width: 95vw
    
    &.large
      width: 95vw
  
  .table
    font-size: 12px
    
    th, td
      padding: 8px
  
  .action-buttons
    flex-direction: column
    
    .btn
      width: 100%
  
  .recurso-form-container
    padding: 20px

@media (max-width: 576px)
  .modal-content
    width: 98vw
  
  .area-recursos
    padding: 15px 10px
    
    h5
      font-size: 16px
  
  .section-header
    font-size: 14px
    padding: 8px 12px

  .card-body-mobile
    padding: 12px
    
    .card-item
      gap: 6px
      margin-bottom: 10px
      
      i
        font-size: 14px
        min-width: 16px
      
      .label
        font-size: 12px
      
      .value
        font-size: 13px
        line-height: 1.5

  .card-header-mobile
    padding: 12px
    
    i
      font-size: 16px
    
    strong
      font-size: 13px

  .card-footer-mobile
    padding: 12px
    
    .btn-block-mobile
      padding: 10px
      font-size: 13px

// Mobile Cards para Recursos Enviados
.mobile-cards-recursos
  display: flex
  flex-direction: column
  gap: 15px
  margin-top: 15px

  @media (min-width: 768px)
    display: none !important

.mobile-card-recurso
  background: #fff
  border-radius: 8px
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1)
  overflow: hidden
  transition: all 0.3s ease
  
  &:hover
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15)
    transform: translateY(-2px)

.card-header-recurso
  background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%)
  padding: 12px 15px
  
  .header-left
    display: flex
    align-items: center
    gap: 10px
    flex-wrap: wrap

.card-body-recurso
  padding: 15px
  
  .card-item
    display: flex
    align-items: flex-start
    margin-bottom: 12px
    gap: 8px
    flex-wrap: wrap
    
    &:last-child
      margin-bottom: 0
    
    .label
      font-weight: 600
      color: #6c757d
      font-size: 13px
      flex-shrink: 0
      min-width: fit-content
      display: flex
      align-items: center
      gap: 6px
      
      i
        color: #007bff
        font-size: 14px
        width: 16px
    
    .value
      flex: 1
      color: #2c3e50
      font-size: 14px
      line-height: 1.4
      text-align: left
      word-break: break-word
      min-width: 0
      overflow-wrap: break-word
      
      .motivo-text
        margin: 0
        font-size: 13px
        color: #2c3e50
        line-height: 1.4
      
      .btn-ver-mais
        margin-top: 8px
        width: 100%
        text-align: left
        padding: 6px 10px
        background: #e7f3ff
        border: 1px solid #b3d9ff
        border-radius: 4px
        color: #007bff
        font-size: 12px
        
        &:hover
          background: #cce7ff
          border-color: #80c7ff

.card-footer-recurso
  padding: 15px
  background: #f8f9fa
  border-top: 1px solid #e9ecef
  display: flex
  flex-direction: column
  gap: 10px
  
  .btn-block-mobile
    width: 100%
    padding: 12px
    font-size: 14px
    font-weight: 600
    display: flex
    align-items: center
    justify-content: center
    gap: 8px
    
    i
      font-size: 16px

// Responsividade adicional para cards de recursos
@media (max-width: 576px)
  .card-body-recurso
    padding: 12px
    
    .card-item
      gap: 6px
      margin-bottom: 10px
      flex-direction: column
      align-items: flex-start
      
      .label
        width: 100%
        font-size: 12px
        
        i
          font-size: 13px
          width: 14px
      
      .value
        width: 100%
        font-size: 13px
        padding-left: 22px
        line-height: 1.5

  .card-header-recurso
    padding: 10px 12px
    
    .header-left
      flex-direction: column
      align-items: flex-start
      gap: 8px

  .card-footer-recurso
    padding: 12px
    
    .btn-block-mobile
      padding: 10px
      font-size: 13px
</style>
